import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CategoriaConta, ContaPagar, ContaRecorrente, PaymentMethod,
  ResultadoDoCancelamento, TipoDeAnexo,
} from '@core/types';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import CartaoKPI from '../componentes/CartaoKPI';
import FeedDeLotes from '../componentes/FeedDeLotes';
import Segmentado from '../componentes/Segmentado';
import {
  contasAPagar, desfazerPagamento, excluirConta, excluirContaRecorrente,
  cancelarLote, folhaNasContas, historicoDeLotes, termosDaEquipe,
  mudarVagaDoAnexo, obterComprovanteConta, pagarConta, pagarContas, reativarConta, removerComprovanteConta,
  salvarCodigoBarrasConta, salvarComprovanteConta, contasRecorrentes,
} from '../lib/consultas';
// 095 — import à parte: o bloco de cima é vizinho do PR do salário nas contas,
// aberto em paralelo, e mexer nele travaria o merge dos dois.
import { desfazerCompraNaHora } from '../lib/consultas';
import { Link } from 'react-router';
import { abrirComprovante, prepararComprovante } from '../lib/comprovante';
import { conferirCodigo, formatarLinha, soDigitos } from '../lib/codigoBarras';
import { copiar } from '../lib/pedidoWhatsApp';
import { linkWhatsApp } from '../lib/orcamento';
import {
  agruparPorVencimento, atalhoDeVencimento, ehSalario, pagaveisNoLote, salarioNaLista, totalSelecionado,
  valorDaConta, type AtalhoVencimento, type SalarioNaLista,
} from '../lib/contasPagar';
import { useFolhaPrevista } from '../lib/useFolhaPrevista';
import { brl, hojeISO, paraNumero } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';
import {
  CATEGORIAS, FormularioConta, FormularioRegra, rotulo, type PreenchidoPeloBoleto,
} from './ContasPagarFormularios';
import { CadastroPeloBoleto } from './ContasPagarBoleto';
import { LoteDoDia } from './ContasPagarLote';
import { DetalheDaConta, type AcoesDoAnexo } from './ContasPagarDetalhe';
import { EnvioDaLista } from './ContasPagarEnvio';
import { LinhaDeSalario } from './ContasPagarSalario';

// O que a loja deve, com data (migração 039). Só o dono — a RLS diz o mesmo.
//
// O SALÁRIO ESTÁ AQUI DESDE A 094 (08/10/2026), mas não MORA aqui: cada linha
// da folha (payroll_entries) aparece ao lado das contas, com a mesma cara, e
// pagar é a pagar_folha do Painel. Nada de salário em contas_pagar — o fluxo
// de caixa já soma a folha em aberto, e uma cópia seria contada duas vezes.
//
// Duas listas na mesma tela porque são duas coisas diferentes: a REGRA ("todo
// dia 10, ~R$ 2.800") e as OCORRÊNCIAS, que é o que se paga. A tela abre nas
// ocorrências, que é o trabalho do dia; as regras ficam atrás de um botão.

const dataBR = (iso: string) => iso.split('-').reverse().join('/');

/** As duas vagas de anexo da conta (080), na ordem em que a conta vive: o
 *  boleto chega primeiro, o comprovante vem com o pagamento. */
const TIPOS_DE_ANEXO: ReadonlyArray<{ tipo: TipoDeAnexo; curto: string; rotulo: string }> = [
  { tipo: 'boleto', curto: 'boleto', rotulo: 'Boleto' },
  { tipo: 'comprovante', curto: 'comprovante', rotulo: 'Comprovante' },
];

// As formas que pagam uma CONTA. 'fiado' fica de fora: dívida da loja não se
// paga fiado — é a única do enum que não faz sentido aqui.
const FORMAS_DO_LOTE: ReadonlyArray<{ id: PaymentMethod; rotulo: string }> = [
  { id: 'dinheiro', rotulo: 'Dinheiro' },
  { id: 'pix', rotulo: 'PIX' },
  { id: 'debito', rotulo: 'Débito' },
  { id: 'credito', rotulo: 'Crédito' },
  { id: 'transferencia', rotulo: 'Transferência' },
  { id: 'boleto', rotulo: 'Boleto' },
];

/** A MINI-TABELA do Grafite: as contas de um dia (ou do mês, em Pagas) num
 *  cartão só, com o fio entre as linhas — não uma pilha de cartões. A linha do
 *  SALÁRIO (ContasPagarSalario.tsx) chega como `li.cartao` com a borda dela;
 *  as variantes do pai a deixam igual às outras sem mexer naquele arquivo: o
 *  cartão vira linha e o fio de cima é o mesmo de todas. */
const MINI_TABELA = 'cartao overflow-hidden [&>li]:rounded-none [&>li]:border-0 [&>li]:bg-transparent '
  + '[&>li+li]:border-t [&>li+li]:border-fio '
  // O CENTRO da linha (o nome da conta) pede 16rem antes de dividir a linha
  // com as ações: sem isto o `flex-1` (base 0) espremia o nome numa coluna de
  // uma palavra, e "Fornecedores" vazava por cima do selo (medido a 1440 px).
  // Com a base, as ações descem para a linha de baixo quando não cabem.
  + '[&>li>.flex-1]:basis-64 '
  // A borda vermelha que o `border-0` tira da linha de salário atrasada volta
  // como PONTO de atraso, no vão da caixinha (index.css).
  + 'sinal-da-linha-atrasada';

/** As ações de texto da linha (editar, excluir, desfazer, anexos): terciárias,
 *  só texto — o vermelho fica para a confirmação, no `botao-perigo`. 44 px de
 *  alvo no celular. */
const LINK_DA_LINHA = 'inline-flex min-h-11 items-center text-xs text-dim underline hover:text-paper sm:min-h-0';

/** O ALVO da caixinha do lote: o quadrado continua de 16 px, e o rótulo em volta
 *  dá 44 px ao dedo no celular. A margem negativa devolve o espaço do
 *  enchimento — a linha não cresce nem desalinha. No computador, o de sempre. */
const ALVO_DA_CAIXA = '-my-3.5 -ml-3 inline-flex shrink-0 cursor-pointer py-3.5 pl-3 pr-3.5 '
  + 'sm:m-0 sm:p-0';

/** A caixa de "tem certeza?" (excluir, desfazer): neutra; quem leva o sinal é
 *  o botão de confirmar, que é o `botao-perigo`. */
const CAIXA_DE_CONFIRMAR = 'mt-2 w-full space-y-2 rounded-[0.625rem] border border-edge-forte bg-panel-alt p-3';

/** O SELO da situação da conta (Grafite): a cor nunca anda sozinha — vai com a
 *  palavra. Paga, atrasada (venceu e não foi paga), vence hoje ou a vencer. */
export function seloDaConta(c: { pago_em: string | null; vencimento: string }, hoje: string):
  { texto: string; classe: string } {
  if (c.pago_em) return { texto: 'paga', classe: 'selo selo-pago' };
  if (c.vencimento < hoje) return { texto: 'atrasada', classe: 'selo selo-atrasado' };
  if (c.vencimento === hoje) return { texto: 'vence hoje', classe: 'selo selo-hoje' };
  return { texto: 'a vencer', classe: 'selo selo-esperado' };
}

export default function ContasPagar() {
  const cliente = useQueryClient();
  const [aba, setAba] = useState<'abertas' | 'pagas' | 'regras' | 'canceladas'>('abertas');
  const [editando, setEditando] = useState<ContaPagar | null | undefined>(undefined);
  const [editandoRegra, setEditandoRegra] = useState<ContaRecorrente | null | undefined>(undefined);
  const [pagando, setPagando] = useState<ContaPagar | null>(null);
  // O histórico é POR MÊS (041, pedido do dono): "o que foi pago e o que não
  // foi" só responde alguma coisa dentro de um período. Nas abas de histórico
  // o mês manda; em "A pagar" a lista é tudo o que está aberto, porque conta
  // vencida de março continua sendo problema em agosto.
  const [mes, setMes] = useState(() => hojeISO().slice(0, 7));
  // A SELEÇÃO (23/08/2026, pedido do dono): marcar várias e pagar de uma vez.
  // Um Set de ids, zerado sempre que a aba ou um filtro muda — conta que o
  // filtro escondeu continuaria pagável em silêncio, inaceitável com dinheiro.
  const [selecao, setSelecao] = useState<Set<string>>(new Set());
  const [pagandoLote, setPagandoLote] = useState(false);
  // Desfazer apaga um lançamento do caixa — merece os mesmos 2 passos que o
  // excluir sempre teve (a inversão de gravidade era dívida catalogada).
  const [desfazendo, setDesfazendo] = useState<string | null>(null);
  // O CÓDIGO DE BARRAS (077, pedido do dono em 18/09/2026): tocar no centro
  // da linha abre o código embaixo, para copiar e mandar a quem paga no banco.
  // Um aberto por vez — a lista é longa e dois painéis iguais confundem.
  const [codigoAberto, setCodigoAberto] = useState<string | null>(null);
  // O CADASTRO PELO BOLETO (18/09/2026): lê a foto/PDF, confere repetição e
  // abre o formulário de sempre já preenchido (ContasPagarBoleto).
  const [peloBoleto, setPeloBoleto] = useState(false);
  // O PDF com tudo o que foi fotografado no dia (25/09/2026).
  const [emLote, setEmLote] = useState(false);
  const [preenchido, setPreenchido] = useState<PreenchidoPeloBoleto | null>(null);
  // MANDAR A LISTA (22/09/2026): o PDF ou as fotos das contas em aberto, para
  // o pai do dono (ContasPagarEnvio).
  const [enviandoLista, setEnviandoLista] = useState(false);

  const contas = useQuery({ queryKey: ['contas-pagar'], queryFn: contasAPagar });
  // A folha do mês nasce ao abrir a tela (094); a leitura espera a geração
  // terminar, mas não depende de ela dar certo.
  const prevista = useFolhaPrevista();
  const folha = useQuery({
    queryKey: ['folha-contas'], queryFn: folhaNasContas, enabled: prevista.pronto,
  });
  // O combinado (só o %, para saber quem tem comissão). A mesma consulta do
  // Painel e da Equipe: o salvar do combinado já invalida ['termos'].
  const termos = useQuery({ queryKey: ['termos'], queryFn: termosDaEquipe });
  const regras = useQuery({
    queryKey: ['contas-recorrentes'], queryFn: contasRecorrentes, enabled: aba === 'regras',
  });

  const recarregar = () => {
    cliente.invalidateQueries({ queryKey: ['contas-pagar'] });
    cliente.invalidateQueries({ queryKey: ['folha-contas'] });
    cliente.invalidateQueries({ queryKey: ['contas-recorrentes'] });
    // A baixa lança saída no caixa — a tela do caixa muda junto.
    cliente.invalidateQueries({ queryKey: ['caixa'] });
  };

  const desfazer = useMutation({ mutationFn: desfazerPagamento, onSuccess: recarregar });
  // A compra paga NA HORA (095) se desfaz APAGANDO: ela nunca esteve "a
  // pagar", e o desfazer_pagamento a recusa com o caminho certo.
  const desfazerNaHora = useMutation({
    mutationFn: desfazerCompraNaHora,
    onSuccess: () => { recarregar(); cliente.invalidateQueries({ queryKey: ['compras-na-hora'] }); },
  });
  // O LOTE: uma data e uma forma para todas; a RPC é atômica (conta já paga
  // ou sem valor derruba o lote inteiro, e a mensagem nomeia a conta).
  const pagarLote = useMutation({
    mutationFn: (p: { data: string; forma: PaymentMethod }) =>
      pagarContas({ ids: [...selecao], data: p.data, forma: p.forma }),
    onSuccess: () => {
      setSelecao(new Set());
      setPagandoLote(false);
      recarregar();
    },
  });
  const [excluindoRegra, setExcluindoRegra] = useState<string | null>(null);
  const excluirRegra = useMutation({
    mutationFn: excluirContaRecorrente,
    onSuccess: () => { setExcluindoRegra(null); recarregar(); },
  });
  const [excluindo, setExcluindo] = useState<string | null>(null);
  const excluir = useMutation({
    mutationFn: excluirConta,
    onSuccess: () => { setExcluindo(null); recarregar(); },
  });
  // OS ANEXOS (044; dois desde a 080, 22/09/2026 — "gostaria de poder anexar
  // na conta ambos"): o BOLETO, que cobra, e o COMPROVANTE, que prova o
  // pagamento. Um input de arquivo só para a tela inteira; os refs guardam DE
  // QUAL conta e QUAL vaga é o anexo em andamento (estado do React chegaria
  // atrasado ao onChange do input).
  const inputComprovante = useRef<HTMLInputElement>(null);
  const contaDoAnexo = useRef<string | null>(null);
  const tipoDoAnexo = useRef<TipoDeAnexo>('comprovante');
  const anexar = useMutation({
    mutationFn: async (arquivo: File) => {
      const contaId = contaDoAnexo.current;
      if (!contaId) return;
      const pronto = await prepararComprovante(arquivo);
      await salvarComprovanteConta({
        contaId, base64: pronto.base64, contentType: pronto.contentType, nome: pronto.nome,
        tipo: tipoDoAnexo.current,
      });
    },
    onSuccess: recarregar,
  });
  const pedirAnexo = (contaId: string, tipo: TipoDeAnexo) => {
    contaDoAnexo.current = contaId;
    tipoDoAnexo.current = tipo;
    inputComprovante.current?.click();
  };
  const subindo = (contaId: string, tipo: TipoDeAnexo) =>
    anexar.isPending && contaDoAnexo.current === contaId && tipoDoAnexo.current === tipo;
  // Enquanto um arquivo sobe para a conta, trocar de lugar ou tirar mexeria
  // na vaga para onde ele está indo.
  const subindoNa = (contaId: string) => anexar.isPending && contaDoAnexo.current === contaId;
  const verComprovante = useMutation({
    mutationFn: async (p: { contaId: string; tipo: TipoDeAnexo }) => {
      const c = await obterComprovanteConta(p.contaId, p.tipo);
      abrirComprovante(c.dados_base64, c.content_type);
    },
  });
  const tirarComprovante = useMutation({
    mutationFn: (p: { contaId: string; tipo: TipoDeAnexo }) => removerComprovanteConta(p.contaId, p.tipo),
    onSuccess: recarregar,
  });
  const mudarVaga = useMutation({
    mutationFn: (p: { contaId: string; tipo: TipoDeAnexo }) => mudarVagaDoAnexo(p.contaId, p.tipo),
    onSuccess: recarregar,
  });
  const ocupadoCom = (m: { isPending: boolean; variables?: { contaId: string; tipo: TipoDeAnexo } },
    contaId: string, tipo: TipoDeAnexo) =>
    m.isPending && m.variables?.contaId === contaId && m.variables?.tipo === tipo;

  /** As escritas das vagas, embrulhadas para o painel do detalhe: ele desenha
   *  os botões junto de CADA documento (25/09/2026, "junta tudo no mesmo
   *  bloco"), e as mutações continuam morando aqui, com o input de arquivo. */
  /** O feed das importações (086). Só do dono, como tudo aqui. */
  const lotes = useQuery({ queryKey: ['lotes'], queryFn: () => historicoDeLotes(20) });
  const [recusas, setRecusas] = useState<ResultadoDoCancelamento[]>([]);
  const [cancelandoLote, setCancelandoLote] = useState<number | null>(null);
  const cancelarLeva = useMutation({
    mutationFn: ({ lote, motivo }: { lote: number; motivo: string }) => cancelarLote(lote, motivo),
    onMutate: ({ lote }) => { setCancelandoLote(lote); setRecusas([]); },
    onSuccess: (r) => {
      // Cancelar não é silencioso: o que ficou de pé volta na lista, com o
      // porquê, em vez de o lote fingir que desfez tudo.
      setRecusas(r.filter((x) => !x.cancelada));
      recarregar();
      void lotes.refetch();
    },
    onSettled: () => setCancelandoLote(null),
  });

  /** Voltar atrás do cancelamento (086). A recusa que importa é a do código de
   *  barras: enquanto a conta estava cancelada, outra pode ter tomado o boleto
   *  — e aí a mensagem do banco nomeia qual. */
  const reativar = useMutation({
    mutationFn: (id: string) => reativarConta(id),
    onSuccess: recarregar,
  });

  const acoesDoAnexo = (c: ContaPagar): AcoesDoAnexo => ({
    anexar: (tipo) => pedirAnexo(c.id, tipo),
    tirar: (tipo) => tirarComprovante.mutate({ contaId: c.id, tipo }),
    mudarVaga: (tipo) => mudarVaga.mutate({ contaId: c.id, tipo }),
    ocupada: (tipo) => anexar.isPending || subindoNa(c.id)
      || ocupadoCom(tirarComprovante, c.id, tipo) || ocupadoCom(mudarVaga, c.id, tipo),
    subindo: (tipo) => subindo(c.id, tipo),
  });

  /** A confirmação do excluir, num lugar só (ela aparecia idêntica em duas
   *  listas).
   *
   *  ELA PRECISA SABER DO ANEXO. O banco recusa apagar conta com boleto ou
   *  comprovante preso — `contas_pagar_comprovantes` cai por cascade (044) e o
   *  arquivo ali é a única cópia —, e a tela tem de dizer isso ANTES do clique,
   *  não devolver erro depois. O caminho é o painel do anexo, que tem o "tirar". */
  const caixaDeExcluir = (c: ContaPagar) => {
    const anexos = TIPOS_DE_ANEXO.filter(({ tipo }) => c[tipo]).map(({ curto }) => curto);
    return (
      <div className={CAIXA_DE_CONFIRMAR}>
        <p className="text-xs text-mute">
          {anexos.length > 0 ? (
            <>
              Esta conta tem {anexos.join(' e ')} anexado, e o arquivo aqui é a
              única cópia — apagar a conta levaria o documento junto. Tire o anexo
              primeiro: toque no centro da conta para abrir o painel dele.
            </>
          ) : (
            <>
              Apaga esta conta de vez — para o caso de digitação errada.
              Se a conta existe mas está adiada, edite o vencimento.
            </>
          )}
        </p>
        <div className="flex gap-2">
          <button type="button" className="botao-secundario flex-1 py-1.5 text-xs"
                  onClick={() => setExcluindo(null)}>Voltar</button>
          <button type="button" className="botao-perigo flex-1 py-1.5 text-xs disabled:opacity-50"
                  disabled={excluir.isPending || anexos.length > 0}
                  onClick={() => excluir.mutate(c.id)}>
            Excluir de vez
          </button>
        </div>
      </div>
    );
  };

  /** Na linha: as duas vagas, curtas. Verde abre; "+ boleto" anexa. Trocar e
   *  tirar ficam no painel que abre tocando no centro da conta. */
  const anexosDaLinha = (c: ContaPagar) => (
    <>
      {TIPOS_DE_ANEXO.map(({ tipo, curto }) => (c[tipo] ? (
        /* Anexado: o PONTO verde diz que existe; o texto é o link (Grafite). */
        <button key={tipo} type="button" className="inline-flex min-h-11 items-center gap-1.5 text-xs text-signal-soft underline hover:text-paper sm:min-h-0"
                disabled={ocupadoCom(verComprovante, c.id, tipo)}
                onClick={() => verComprovante.mutate({ contaId: c.id, tipo })}>
          <span className="ponto text-ok" aria-hidden="true" />
          {curto}
        </button>
      ) : (
        <button key={tipo} type="button" className={LINK_DA_LINHA}
                disabled={anexar.isPending}
                onClick={() => pedirAnexo(c.id, tipo)}>
          {subindo(c.id, tipo) ? 'subindo…' : `+ ${curto}`}
        </button>
      )))}
    </>
  );

  // Filtros (17/08/2026, pedido do dono): categoria, texto e — em "A pagar" —
  // faixa de vencimento. Em "Pagas" a data já é o mês das setas. Os CARTÕES do
  // topo continuam globais de propósito: "vencidas" é fato da loja, não do
  // recorte.
  const [filtroCategoria, setFiltroCategoria] = useState<CategoriaConta | 'todas'>('todas');
  const [filtroTexto, setFiltroTexto] = useState('');
  const [venceDe, setVenceDe] = useState('');
  const [venceAte, setVenceAte] = useState('');

  // Filtro mudou → seleção zera (ver o comentário do estado `selecao`).
  useEffect(() => {
    setSelecao(new Set());
    setPagandoLote(false);
  }, [filtroCategoria, filtroTexto, venceDe, venceAte]);

  // Seleção zerada com o painel aberto (desmarcando conta a conta) deixava
  // `pagandoLote` preso em true: a próxima conta marcada abria o painel de
  // pagamento direto, sem passar pela barra. Fecha junto.
  useEffect(() => {
    if (selecao.size === 0) setPagandoLote(false);
  }, [selecao.size]);

  const hoje = hojeISO();
  const noMes = (iso: string | null) => Boolean(iso && iso.slice(0, 7) === mes);
  const texto = filtroTexto.trim().toLowerCase();
  // 086: TUDO que a tela lista, soma ou conta parte daqui. A conta cancelada
  // continua vindo do banco — o dono precisa poder ver o que cancelou e voltar
  // atrás —, mas ela não é conta a pagar nem conta paga: sai de toda conta.
  // Uma linha só, porque eram TRÊS lugares somando por `pago_em` e nenhum
  // olhando cancelamento (a lista, os dois números do mês e o resumo).
  //
  // O SALÁRIO (094) entra AQUI, junto: lista, grupos por dia, os três cartões
  // e os dois números do mês passam a contá-lo sem uma linha a mais. Ele fica
  // fora só do que é de conta — o lote, o "Pelo boleto" e as canceladas.
  //
  // Quem tem comissão no combinado: a prevista dessa pessoa vale só o
  // salário-base, e a linha não se paga daqui (revisão da 094) — a comissão
  // se confere no Painel. A trava de verdade é do banco (pagar_folha); esta
  // só evita o botão que daria erro. Sem os termos ainda, ninguém fica preso:
  // o banco recusa e o aviso diz o porquê.
  const comComissao = useMemo(
    () => new Set((termos.data ?? [])
      .filter((t) => Number(t.comissao_percent) > 0)
      .map((t) => t.profile_id)),
    [termos.data],
  );
  const salarios = useMemo(
    () => (folha.data ?? [])
      .map((l) => salarioNaLista(l, comComissao))
      .filter((s): s is SalarioNaLista => s !== null),
    [folha.data, comComissao],
  );
  const validas: Array<ContaPagar | SalarioNaLista> = [
    ...(contas.data ?? []).filter((c) => !c.cancelada_em), ...salarios,
  ].sort((a, b) => a.vencimento.localeCompare(b.vencimento));
  const canceladas = (contas.data ?? []).filter((c) => c.cancelada_em);
  const lista = validas
    .filter((c) => (aba === 'pagas' ? noMes(c.pago_em) : !c.pago_em))
    .filter((c) => filtroCategoria === 'todas' || c.categoria === filtroCategoria)
    .filter((c) => !texto || c.descricao.toLowerCase().includes(texto))
    .filter((c) => aba === 'pagas'
      || ((!venceDe || c.vencimento >= venceDe) && (!venceAte || c.vencimento <= venceAte)));
  // O total DO QUE ESTÁ NA TELA (pedido do dono, 19/08/2026): categoria, texto
  // e faixa de vencimento mudam a lista, e o número embaixo tem que acompanhar
  // — senão "quanto gastei com fornecedor em julho?" continua saindo na mão.
  // Na conta paga vale o valor REAL (a luz veio 30 reais mais cara que o
  // previsto; o que importa é o que saiu da conta).
  const totalDaSelecao = lista.reduce((soma, c) => soma + (valorDaConta(c) ?? 0), 0);
  // Quem PODE entrar no lote: abertas com valor definido. Conta "a definir"
  // (046) fica de fora — o lote não tem campo de valor, e um total que
  // ignora contas selecionadas seria um total mentiroso.
  const pagaveis = pagaveisNoLote(lista);
  const totalDoLote = totalSelecionado(lista, selecao);
  const grupos = aba === 'abertas' ? agruparPorVencimento(lista, hoje) : [];
  const alternarConta = (id: string) => setSelecao((atual) => {
    const novo = new Set(atual);
    if (novo.has(id)) novo.delete(id); else novo.add(id);
    return novo;
  });
  // O fechamento do mês em dois números: o que saiu e o que ficou.
  const pagoNoMes = validas
    .filter((c) => noMes(c.pago_em))
    .reduce((soma, c) => soma + (valorDaConta(c) ?? 0), 0);
  const venceNoMes = validas
    .filter((c) => !c.pago_em && c.vencimento.slice(0, 7) === mes)
    .reduce((soma, c) => soma + (valorDaConta(c) ?? 0), 0);
  const mesBR = `${mes.slice(5)}/${mes.slice(0, 4)}`;
  const passoMes = (n: number) => {
    const d = new Date(`${mes}-15T12:00:00`);
    d.setMonth(d.getMonth() + n);
    setMes(hojeISO(d).slice(0, 7));
  };

  // O topo responde a pergunta do dono em um olhar: o que venceu, o que vence
  // esta semana, e quanto é o mês.
  const resumo = useMemo(() => {
    const abertas = validas.filter((c) => !c.pago_em);
    const valor = (c: ContaPagar | SalarioNaLista) => valorDaConta(c) ?? 0;
    const em7 = new Date(`${hoje}T12:00:00`);
    em7.setDate(em7.getDate() + 7);
    const limite = hojeISO(em7);
    return {
      vencidas: abertas.filter((c) => c.vencimento < hoje),
      semana: abertas.filter((c) => c.vencimento >= hoje && c.vencimento <= limite),
      totalAberto: abertas.reduce((s, c) => s + valor(c), 0),
    };
  // `validas` deriva das duas consultas: as duas são dependência.
  }, [contas.data, folha.data, hoje]);

  const erroAcao = [
    desfazer.error, excluirRegra.error, excluir.error,
    anexar.error, verComprovante.error, tirarComprovante.error, mudarVaga.error,
    desfazerNaHora.error,
  ].find(Boolean);

  return (
    <Layout titulo="Contas a pagar">
      {/* A coluna do feed EMPILHA no celular (molde do mapa da loja): o dono lê
          isto no iPhone e precisa alcançar o histórico lá, não perdê-lo. */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_23rem] lg:items-start">
      <div className="space-y-4">
        {/* DUAS colunas no celular, não três (25/08/2026): em 375px o cartão
            de 1/3 dá 83px de texto e "R$ 1.234,56" precisa de 91 — medido no
            navegador. Qualquer valor com milhar era cortado. O total, que é o
            número que o dono procura, ocupa a linha inteira. */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
          <CartaoKPI rotulo="Vencidas" valor={resumo.vencidas.length} numero
                     tom={resumo.vencidas.length ? 'erro' : 'neutro'}
                     detalhe={<span className="numero">{brl(resumo.vencidas.reduce((s, c) => s + (valorDaConta(c) ?? 0), 0))}</span>} />
          <CartaoKPI rotulo="Vencem em 7 dias" valor={resumo.semana.length} numero
                     detalhe={<span className="numero">{brl(resumo.semana.reduce((s, c) => s + (valorDaConta(c) ?? 0), 0))}</span>} />
          <CartaoKPI rotulo="Total em aberto" valor={brl(resumo.totalAberto)} numero
                     detalhe="estimativas incluídas"
                     className="col-span-2 sm:col-span-1" />
        </div>

        {/* Largura inteira, abaixo dos cartões: na barra das abas a fileira de
            botões não quebra linha, e um terceiro botão ali estourava 375 px. */}
        {enviandoLista ? (
          <EnvioDaLista aoFechar={() => setEnviandoLista(false)} />
        ) : (
          <button type="button" className="botao-secundario min-h-11 w-full py-2 text-sm"
                  onClick={() => setEnviandoLista(true)}>
            Mandar a lista das contas (PDF ou fotos)
          </button>
        )}

        <div className="cartao flex flex-wrap items-center justify-between gap-2 p-3">
          <Segmentado toque
            itens={[{ id: 'abertas', rotulo: 'A pagar' }, { id: 'pagas', rotulo: 'Pagas' },
                    { id: 'regras', rotulo: 'Contas fixas' },
                    // 086: sem ver o que cancelou, o dono não consegue voltar
                    // atrás — e aí "cancelar" seria só um apagar mais educado.
                    ...(canceladas.length > 0
                      ? [{ id: 'canceladas' as const, rotulo: `Canceladas (${canceladas.length})` }]
                      : [])] as const}
            valor={aba}
            aoMudar={(k) => {
              setAba(k);
              // Trocar de aba FECHA o formulário aberto (bug real: começar
              // em "Contas fixas", mudar para "A pagar" e o salvar gravava
              // uma REGRA — o formulário antigo continuava vivo e vencia).
              setEditando(undefined);
              setEditandoRegra(undefined);
              setPagando(null);
              setExcluindo(null);
              setExcluindoRegra(null);
              setSelecao(new Set());
              setPagandoLote(false);
              setDesfazendo(null);
              setCodigoAberto(null);
              setPeloBoleto(false);
              setPreenchido(null);
              setEnviandoLista(false);
            }}
          />
          {/* flex-wrap (Grafite, 29/10/2026): na aba Pagas as setas do mês
              entram na fileira, e a 390 px o "+ Conta" saía da tela — medido no
              WebKit, a página rolava de lado (469 px numa tela de 390). */}
          <div className="flex flex-wrap items-center gap-2">
            {aba === 'pagas' && (
              <>
                <button type="button" className="botao-secundario min-h-11 min-w-11 px-2 py-1 sm:min-h-9 sm:min-w-9"
                        aria-label="Mês anterior" onClick={() => passoMes(-1)}>←</button>
                <span className="numero text-sm text-mute">{mesBR}</span>
                <button type="button" className="botao-secundario min-h-11 min-w-11 px-2 py-1 sm:min-h-9 sm:min-w-9"
                        aria-label="Próximo mês" onClick={() => passoMes(1)}>→</button>
              </>
            )}
            {aba !== 'regras' && (
              <button type="button" className="botao-secundario min-h-11 px-3 py-2 text-sm sm:min-h-9"
                      onClick={() => {
                        setEditando(undefined);
                        setPreenchido(null);
                        setPeloBoleto(true);
                        // A conferência de repetição roda no aparelho: a lista
                        // tem que estar fresca (outra pessoa pode ter lançado).
                        cliente.invalidateQueries({ queryKey: ['contas-pagar'] });
                      }}>
                + Pelo boleto
              </button>
            )}
            {/* O PDF do dia inteiro (25/09/2026): o dono fotografa tudo de uma
                vez no CamScanner, e a tela separa folha por folha. */}
            {aba !== 'regras' && (
              <button type="button" className="botao-secundario min-h-11 px-3 py-2 text-sm sm:min-h-9"
                      onClick={() => {
                        setEditando(undefined);
                        setPreenchido(null);
                        setPeloBoleto(false);
                        setEmLote(true);
                        cliente.invalidateQueries({ queryKey: ['contas-pagar'] });
                      }}>
                + Documentos do dia
              </button>
            )}
            <button type="button" className="botao-primario min-h-11 px-3 py-2 text-sm sm:min-h-9"
                    onClick={() => {
                      setPeloBoleto(false);
                      setPreenchido(null);
                      if (aba === 'regras') setEditandoRegra(null); else setEditando(null);
                    }}>
              {aba === 'regras' ? '+ Conta fixa' : '+ Conta'}
            </button>
          </div>
        </div>

        {aba !== 'regras' && (
          <div className="cartao grid gap-2 p-3 sm:grid-cols-4">
            <div>
              <label className="rotulo" htmlFor="fc-categoria">Categoria</label>
              <select id="fc-categoria" className="campo" value={filtroCategoria}
                      onChange={(e) => setFiltroCategoria(e.target.value as CategoriaConta | 'todas')}>
                <option value="todas">Todas</option>
                {CATEGORIAS.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="rotulo" htmlFor="fc-texto">Buscar</label>
              <input id="fc-texto" className="campo" placeholder="descrição…"
                     value={filtroTexto} onChange={(e) => setFiltroTexto(e.target.value)} />
            </div>
            {aba === 'abertas' && (
              <>
                <div className="sm:col-span-4">
                  <p className="rotulo">Vencimento</p>
                  {/* Os chips só preenchem o De/Até de baixo — nenhum estado
                      paralelo para um dia divergir do outro. O ativo é DERIVADO
                      da faixa: mexer no De/Até à mão apaga o chip sozinho. */}
                  <Segmentado<AtalhoVencimento | ''> toque
                    itens={[{ id: 'hoje', rotulo: 'Hoje' }, { id: 'semana', rotulo: 'Esta semana' },
                            { id: 'quinze', rotulo: 'Próximos 15 dias' }, { id: 'tudo', rotulo: 'Tudo' }]}
                    valor={(['hoje', 'semana', 'quinze', 'tudo'] as const).find((id) => {
                      const faixa = atalhoDeVencimento(id, hoje);
                      return venceDe === faixa.de && venceAte === faixa.ate;
                    }) ?? ''}
                    aoMudar={(id) => {
                      if (!id) return;
                      const faixa = atalhoDeVencimento(id, hoje);
                      setVenceDe(faixa.de);
                      setVenceAte(faixa.ate);
                    }}
                  />
                </div>
                <div>
                  <label className="rotulo" htmlFor="fc-de">Vence de</label>
                  <input id="fc-de" type="date" className="campo" value={venceDe}
                         onChange={(e) => setVenceDe(e.target.value)} />
                </div>
                <div>
                  <label className="rotulo" htmlFor="fc-ate">Vence até</label>
                  <input id="fc-ate" type="date" className="campo" value={venceAte}
                         onChange={(e) => setVenceAte(e.target.value)} />
                </div>
              </>
            )}
          </div>
        )}

        {aba === 'pagas' && (
          <div className="cartao flex flex-wrap items-center justify-between gap-3 p-3">
            <p className="text-sm">
              <span className="text-dim">Pago em {mesBR}:</span>{' '}
              <strong className="numero font-medium">{brl(pagoNoMes)}</strong>
            </p>
            {/* O total do FILTRO. Some quando ele não muda nada, para não
                repetir o número do lado e dar a impressão de dois totais. */}
            {(filtroCategoria !== 'todas' || texto) && (
              <p className="text-sm">
                <span className="text-dim">Nesta seleção ({lista.length}):</span>{' '}
                <strong className="numero font-medium text-signal-soft">{brl(totalDaSelecao)}</strong>
              </p>
            )}
            <p className="text-sm">
              <span className="text-dim">Ainda aberto com vencimento no mês:</span>{' '}
              <strong className={`numero font-medium ${venceNoMes > 0 ? 'text-alerta' : ''}`}>
                {brl(venceNoMes)}
              </strong>
            </p>
            {/* 095: as compras pagas na hora estão nesta lista (selo "na
                hora"); o resumo delas — por forma e por fornecedor — mora na
                tela própria. */}
            <Link to="/compra-na-hora" className="inline-flex min-h-11 items-center text-xs text-dim underline sm:min-h-0">
              ver compras na hora do mês →
            </Link>
          </div>
        )}

        {peloBoleto && (
          <CadastroPeloBoleto contas={contas.data ?? []}
                              aoFechar={() => setPeloBoleto(false)}
                              aoMudar={recarregar}
                              aoPreencher={(p) => { setPeloBoleto(false); setPreenchido(p); setEditando(null); }} />
        )}
        {emLote && (
          <LoteDoDia contas={contas.data ?? []}
                     aoFechar={() => setEmLote(false)}
                     aoMudar={recarregar}
                     aoPreencher={(p) => { setEmLote(false); setPreenchido(p); setEditando(null); }} />
        )}
        {editando !== undefined && (
          <FormularioConta key={editando?.id ?? (preenchido ? `boleto-${preenchido.codigo}` : 'nova')}
                           conta={editando} contas={contas.data ?? []} preenchido={preenchido}
                           aoFechar={() => { setEditando(undefined); setPreenchido(null); recarregar(); }}
                           aoSalvar={() => { setEditando(undefined); setPreenchido(null); recarregar(); }} />
        )}
        {editandoRegra !== undefined && (
          <FormularioRegra regra={editandoRegra} aoFechar={() => setEditandoRegra(undefined)}
                           aoSalvar={() => { setEditandoRegra(undefined); recarregar(); }} />
        )}
        {pagando && (
          <PainelPagamento key={pagando.id} conta={pagando} aoFechar={() => setPagando(null)}
                           aoMudar={recarregar}
                           aoPagar={() => { setPagando(null); recarregar(); }} />
        )}

        {contas.isPending ? <Carregando /> : null}
        {contas.error ? <Aviso tipo="erro">{mensagemDeErro(contas.error)}</Aviso> : null}
        {/* A folha é outra consulta: se ela falhar, as contas continuam — e o
            aviso diz que os salários é que faltam, não que não há salário. */}
        {folha.error ? (
          <Aviso tipo="erro">Os salários não carregaram: {mensagemDeErro(folha.error)}</Aviso>
        ) : null}
        {prevista.erro ? (
          <Aviso tipo="alerta">
            A folha do mês não foi gerada agora ({mensagemDeErro(prevista.erro)}). Os
            salários que já estavam lançados continuam aqui; os que faltam, lance no Painel.
          </Aviso>
        ) : null}
        {/* Um aviso para as ações de linha — eram sete empilhados, e cada
            mutation nova acrescentava outro. O erro da CONSULTA fica
            separado: é outro ciclo de vida. */}
        {erroAcao ? <Aviso tipo="erro">{mensagemDeErro(erroAcao)}</Aviso> : null}
        {/* Sem teto de 1000 desde 09/10/2026: contasAPagar busca em páginas,
            e os totais somam a lista inteira (ver a consulta). */}

        {/* Um input para todas as linhas — quem diz de qual conta e de qual
            vaga é o anexo são contaDoAnexo e tipoDoAnexo (pedirAnexo). */}
        <input ref={inputComprovante} type="file" className="hidden"
               accept="image/*,application/pdf"
               onChange={(e) => {
                 const arquivo = e.target.files?.[0];
                 if (arquivo) anexar.mutate(arquivo);
                 e.target.value = '';
               }} />

        {aba !== 'regras' && lista.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-dim">
            <p>
              {lista.length} conta{lista.length > 1 ? 's' : ''} na lista ·{' '}
              <strong className="numero font-medium text-paper">{brl(totalDaSelecao)}</strong>
            </p>
            {/* Selecionar TODAS as visíveis — opera sobre a lista já filtrada,
                então respeita os filtros por construção. Indeterminado quando
                só parte está marcada. */}
            {aba === 'abertas' && pagaveis.length > 0 && (
              <label className="flex min-h-11 cursor-pointer items-center gap-1.5 sm:min-h-0">
                <input type="checkbox" className="size-4"
                       checked={pagaveis.every((c) => selecao.has(c.id))}
                       ref={(el) => {
                         if (el) {
                           el.indeterminate = selecao.size > 0
                             && !pagaveis.every((c) => selecao.has(c.id));
                         }
                       }}
                       onChange={(e) => setSelecao(
                         e.target.checked ? new Set(pagaveis.map((c) => c.id)) : new Set(),
                       )} />
                selecionar visíveis ({pagaveis.length})
              </label>
            )}
          </div>
        )}

        {aba === 'canceladas' ? (
          /* O QUE FOI CANCELADO, e o caminho de volta. É esta tela que faz o
             cancelar ser cancelamento e não exclusão: sem ver, não há como
             desfazer — e o documento anexado continua aqui, intacto. */
          <ul className="space-y-2">
            {canceladas.map((c) => (
              <li key={c.id} className="cartao flex flex-wrap items-center justify-between gap-3 p-3 opacity-80">
                <div className="min-w-0">
                  <p className="text-sm font-medium line-through decoration-mute">{c.descricao}</p>
                  <p className="text-xs text-mute">
                    {rotulo(c.categoria)} · vencia {dataBR(c.vencimento)}
                    {c.lote_id ? ' · veio de um PDF do dia' : ''}
                  </p>
                  <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-xs text-mute">
                    <span className="selo">cancelada</span>
                    Cancelada{c.cancelada_em ? ` em ${dataBR(c.cancelada_em.slice(0, 10))}` : ''}
                    {c.cancelada_por_nome ? ` por ${c.cancelada_por_nome}` : ''}
                    {c.motivo_cancelamento ? ` — ${c.motivo_cancelamento}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="numero text-sm text-dim line-through">
                    {brl(valorDaConta(c) ?? 0)}
                  </span>
                  <button type="button" className="botao-terciario text-xs"
                          disabled={reativar.isPending}
                          onClick={() => reativar.mutate(c.id)}>
                    {reativar.isPending ? 'Reativando…' : 'Reativar'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : aba === 'regras' ? (
          <ul className="space-y-2">
            {(regras.data ?? []).map((r) => (
              <li key={r.id} className={`cartao flex flex-wrap items-center justify-between gap-2 p-3 ${
                !r.ativa ? 'opacity-50' : ''}`}>
                <div>
                  <p className="text-sm font-medium">
                    {r.descricao} {!r.ativa && <span className="text-xs text-dim">(desativada)</span>}
                  </p>
                  <p className="text-xs text-mute">
                    {rotulo(r.categoria)} · todo dia {r.dia_vencimento} ·{' '}
                    {r.valor_previsto == null ? 'valor variável' : `~${brl(Number(r.valor_previsto))}`}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button type="button" className={LINK_DA_LINHA}
                          onClick={() => setEditandoRegra(r)}>editar</button>
                  <button type="button" className={LINK_DA_LINHA}
                          disabled={excluirRegra.isPending}
                          onClick={() => setExcluindoRegra(
                            excluindoRegra === r.id ? null : r.id)}>
                    excluir
                  </button>
                </div>
                {excluindoRegra === r.id && (
                  <div className={CAIXA_DE_CONFIRMAR}>
                    <p className="text-xs text-mute">
                      Some a regra e as parcelas futuras <strong>que ninguém tocou</strong>.
                      O que já foi pago fica no histórico. Para pausar sem apagar,
                      use o "Ativa" na edição.
                    </p>
                    <div className="flex gap-2">
                      <button type="button" className="botao-secundario flex-1 py-1.5 text-xs"
                              onClick={() => setExcluindoRegra(null)}>Voltar</button>
                      <button type="button" className="botao-perigo flex-1 py-1.5 text-xs"
                              disabled={excluirRegra.isPending}
                              onClick={() => excluirRegra.mutate(r.id)}>
                        Excluir de vez
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
            {(regras.data ?? []).length === 0 && !regras.isPending && (
              <li className="text-sm text-dim">
                Nenhuma conta fixa. Cadastre aluguel, luz, internet… uma vez — o sistema
                gera os próximos 12 meses sozinho. Os salários entram sozinhos, pela folha.
              </li>
            )}
          </ul>
        ) : (
          <>
            {/* O EXTRATO DO FUTURO (23/08/2026): a aba de abertas agrupa por
                dia de vencimento — "visualizar o vencimento de uma data
                específica" vira ler a lista rolando, sem calendário. O
                checkbox do CABEÇALHO seleciona as pagáveis do dia inteiro:
                o fluxo do dono ("pagar tudo do dia 25") em dois toques. */}
            {aba === 'abertas' ? (
              <div className="space-y-4">
                {grupos.map((g) => {
                  const pagaveisDoDia = pagaveisNoLote(g.contas);
                  const todasDoDia = pagaveisDoDia.length > 0
                    && pagaveisDoDia.every((c) => selecao.has(c.id));
                  return (
                    <section key={g.dia}>
                      <header className="mb-1.5 flex flex-wrap items-center gap-2 px-1 pb-1">
                        <label className={ALVO_DA_CAIXA}>
                          <input type="checkbox" className="size-4"
                                 checked={todasDoDia}
                                 disabled={pagaveisDoDia.length === 0}
                                 aria-label={`Selecionar todas de ${g.rotulo}`}
                                 onChange={() => setSelecao((atual) => {
                                   const novo2 = new Set(atual);
                                   if (todasDoDia) pagaveisDoDia.forEach((c) => novo2.delete(c.id));
                                   else pagaveisDoDia.forEach((c) => novo2.add(c.id));
                                   return novo2;
                                 })} />
                        </label>
                        {/* O dia leva o PONTO do sinal; a palavra está no selo de
                            cada conta (Grafite). */}
                        <span className="flex items-center gap-2 text-sm font-medium">
                          {g.atrasado && <span className="ponto text-erro" aria-hidden="true" />}
                          {g.dia === hoje && <span className="ponto text-alerta" aria-hidden="true" />}
                          {g.rotulo}
                        </span>
                        <span className="text-xs text-dim">
                          {g.contas.length} conta{g.contas.length > 1 ? 's' : ''}
                        </span>
                        <span className="numero ml-auto text-sm text-paper">
                          {brl(g.subtotal)}
                          {g.semValor > 0 && (
                            <span className="ml-1 font-sans text-xs font-normal text-dim">
                              +{g.semValor} a definir
                            </span>
                          )}
                        </span>
                      </header>
                      <ul className={MINI_TABELA}>
                        {g.contas.map((c) => {
              if (ehSalario(c)) {
                return <LinhaDeSalario key={`folha-${c.id}`} salario={c} aba="abertas" hoje={hoje} />;
              }
              const selo = seloDaConta(c, hoje);
              const estimada = c.valor_real === null;
              return (
                <li key={c.id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 p-3">
                  {/* O checkbox do lote (23/08/2026). Só na aba de abertas, e
                      DESABILITADO na conta sem valor: o lote não tem campo de
                      valor, e selecionável-com-aviso faria o total mentir. */}
                  {aba === 'abertas' && (
                    <label className={ALVO_DA_CAIXA}>
                      <input type="checkbox" className="size-4"
                             checked={selecao.has(c.id)}
                             disabled={valorDaConta(c) === null}
                             title={valorDaConta(c) === null
                               ? 'Defina o valor antes de pagar em lote' : undefined}
                             aria-label={`Selecionar ${c.descricao}`}
                             onChange={() => alternarConta(c.id)} />
                    </label>
                  )}
                  <CentroDaConta conta={c} aberto={codigoAberto === c.id}
                                 aoTocar={() => setCodigoAberto(codigoAberto === c.id ? null : c.id)} />
                  {/* flex-wrap (22/09/2026): com as duas vagas de anexo, a 375 px o
                      "Pagar" saía da tela — agora as ações descem de linha. */}
                  <div className="ml-auto flex flex-wrap items-center justify-end gap-3 gap-y-1">
                    <span className={selo.classe}>{selo.texto}</span>
                    <span className={`numero text-base sm:text-lg ${estimada ? 'italic text-mute' : 'text-paper'}`}>
                      {c.valor_real == null && c.valor_previsto == null
                        ? <span className="font-sans text-sm">a definir</span>
                        : <>{estimada ? '~' : ''}{brl(Number(c.valor_real ?? c.valor_previsto))}</>}
                    </span>
                    {anexosDaLinha(c)}
                    {c.pago_em ? (
                      <button type="button" className={LINK_DA_LINHA}
                              onClick={() => setDesfazendo(desfazendo === c.id ? null : c.id)}>
                        desfazer
                      </button>
                    ) : (
                      <>
                        <button type="button" className={LINK_DA_LINHA}
                                onClick={() => setEditando(c)}>editar</button>
                        {/* Só a AVULSA: ocorrência de regra o gerador recriaria —
                            o caminho é a regra, e o banco diz isso se tentarem. */}
                        {!c.recorrencia_id && (
                          <button type="button" className={LINK_DA_LINHA}
                                  onClick={() => setExcluindo(excluindo === c.id ? null : c.id)}>
                            excluir
                          </button>
                        )}
                        <button type="button" className="botao-secundario min-h-11 px-3 py-1 text-xs sm:min-h-8"
                                onClick={() => setPagando(c)}>Pagar</button>
                      </>
                    )}
                  </div>
                  {codigoAberto === c.id && (
                    <>
                      <DetalheDaConta conta={c} acoes={acoesDoAnexo(c)}>
                        <PainelCodigoBarras conta={c} aoFechar={() => setCodigoAberto(null)}
                                            aoMudar={recarregar} />
                      </DetalheDaConta>
                    </>
                  )}
                  {/* Desfazer APAGA o lançamento do caixa — 2 passos, como o
                      excluir sempre teve. Era 1 clique: inversão de gravidade. */}
                  {desfazendo === c.id && (
                    <div className={CAIXA_DE_CONFIRMAR}>
                      <p className="text-xs text-mute">
                        {/* 095: a compra paga na hora nunca esteve "a pagar" —
                            desfazer a APAGA (conta e lançamento). */}
                        {c.paga_na_hora === true
                          ? 'Apaga esta compra: sai do caixa e desta lista. Com comprovante anexado, tire o anexo antes.'
                          : <>Apaga o lançamento do caixa e devolve a conta para
                            "A pagar". O boleto e o comprovante anexados ficam.</>}
                      </p>
                      <div className="flex gap-2">
                        <button type="button" className="botao-secundario flex-1 py-1.5 text-xs"
                                onClick={() => setDesfazendo(null)}>Voltar</button>
                        <button type="button" className="botao-perigo flex-1 py-1.5 text-xs"
                                disabled={desfazer.isPending || desfazerNaHora.isPending}
                                onClick={() => (c.paga_na_hora === true ? desfazerNaHora : desfazer).mutate(c.id, {
                                  onSuccess: () => setDesfazendo(null),
                                })}>
                          {c.paga_na_hora === true ? 'Apagar a compra' : 'Desfazer o pagamento'}
                        </button>
                      </div>
                    </div>
                  )}
                  {excluindo === c.id && caixaDeExcluir(c)}
                </li>
              );
                        })}
                      </ul>
                    </section>
                  );
                })}
                {lista.length === 0 && !contas.isPending && (
                  <p className="text-sm text-dim">Nada em aberto.</p>
                )}
              </div>
            ) : (
              <ul className={MINI_TABELA}>
                {lista.map((c) => {
              if (ehSalario(c)) {
                return <LinhaDeSalario key={`folha-${c.id}`} salario={c} aba="pagas" hoje={hoje} />;
              }
              const selo = seloDaConta(c, hoje);
              const estimada = c.valor_real === null;
              return (
                <li key={c.id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 p-3">
                  <CentroDaConta conta={c} aberto={codigoAberto === c.id}
                                 aoTocar={() => setCodigoAberto(codigoAberto === c.id ? null : c.id)} />
                  {/* flex-wrap (22/09/2026): com as duas vagas de anexo, a 375 px o
                      "Pagar" saía da tela — agora as ações descem de linha. */}
                  <div className="ml-auto flex flex-wrap items-center justify-end gap-3 gap-y-1">
                    <span className={selo.classe}>{selo.texto}</span>
                    <span className={`numero text-base sm:text-lg ${estimada ? 'italic text-mute' : 'text-paper'}`}>
                      {c.valor_real == null && c.valor_previsto == null
                        ? <span className="font-sans text-sm">a definir</span>
                        : <>{estimada ? '~' : ''}{brl(Number(c.valor_real ?? c.valor_previsto))}</>}
                    </span>
                    {anexosDaLinha(c)}
                    {c.pago_em ? (
                      <button type="button" className={LINK_DA_LINHA}
                              onClick={() => setDesfazendo(desfazendo === c.id ? null : c.id)}>
                        desfazer
                      </button>
                    ) : (
                      <>
                        <button type="button" className={LINK_DA_LINHA}
                                onClick={() => setEditando(c)}>editar</button>
                        {/* Só a AVULSA: ocorrência de regra o gerador recriaria —
                            o caminho é a regra, e o banco diz isso se tentarem. */}
                        {!c.recorrencia_id && (
                          <button type="button" className={LINK_DA_LINHA}
                                  onClick={() => setExcluindo(excluindo === c.id ? null : c.id)}>
                            excluir
                          </button>
                        )}
                        <button type="button" className="botao-secundario min-h-11 px-3 py-1 text-xs sm:min-h-8"
                                onClick={() => setPagando(c)}>Pagar</button>
                      </>
                    )}
                  </div>
                  {codigoAberto === c.id && (
                    <>
                      <DetalheDaConta conta={c} acoes={acoesDoAnexo(c)}>
                        <PainelCodigoBarras conta={c} aoFechar={() => setCodigoAberto(null)}
                                            aoMudar={recarregar} />
                      </DetalheDaConta>
                    </>
                  )}
                  {/* Desfazer APAGA o lançamento do caixa — 2 passos, como o
                      excluir sempre teve. Era 1 clique: inversão de gravidade. */}
                  {desfazendo === c.id && (
                    <div className={CAIXA_DE_CONFIRMAR}>
                      <p className="text-xs text-mute">
                        {/* 095: a compra paga na hora nunca esteve "a pagar" —
                            desfazer a APAGA (conta e lançamento). */}
                        {c.paga_na_hora === true
                          ? 'Apaga esta compra: sai do caixa e desta lista. Com comprovante anexado, tire o anexo antes.'
                          : <>Apaga o lançamento do caixa e devolve a conta para
                            "A pagar". O boleto e o comprovante anexados ficam.</>}
                      </p>
                      <div className="flex gap-2">
                        <button type="button" className="botao-secundario flex-1 py-1.5 text-xs"
                                onClick={() => setDesfazendo(null)}>Voltar</button>
                        <button type="button" className="botao-perigo flex-1 py-1.5 text-xs"
                                disabled={desfazer.isPending || desfazerNaHora.isPending}
                                onClick={() => (c.paga_na_hora === true ? desfazerNaHora : desfazer).mutate(c.id, {
                                  onSuccess: () => setDesfazendo(null),
                                })}>
                          {c.paga_na_hora === true ? 'Apagar a compra' : 'Desfazer o pagamento'}
                        </button>
                      </div>
                    </div>
                  )}
                  {excluindo === c.id && caixaDeExcluir(c)}
                </li>
              );
                })}
                {lista.length === 0 && !contas.isPending && (
                  <li className="p-3 text-sm text-dim">Nenhuma conta paga em {mesBR}.</li>
                )}
              </ul>
            )}

            {/* A BARRA DO LOTE: fixa acima do nav inferior (mesmo truque do
                BarraCarrinho — o nav é fixed bottom-0). Aparece com a
                primeira seleção e traz o total na cara. No computador (lg)
                a barra de baixo não existe (Grafite, 29/10/2026): a faixa
                desce ao pé da tela e começa depois do menu lateral (w-60).
                O `mb-0` tira a margem que o space-y-4 do pai dá a todo filho
                menos o último: em elemento fixo ela não abre espaço, ela
                SOBE a faixa 16 px (medido no WebKit e no Chromium). */}
            {aba === 'abertas' && selecao.size > 0 && !pagandoLote && (
              <div className="fixed inset-x-0 bottom-[calc(3.75rem+env(safe-area-inset-bottom))] z-20 mb-0 border-t border-edge-forte bg-panel px-4 py-2.5 shadow-lg print:hidden lg:bottom-0 lg:left-60">
                <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2">
                  <span className="text-sm">
                    <strong className="numero font-medium">{selecao.size}</strong> selecionada{selecao.size > 1 ? 's' : ''} ·{' '}
                    <strong className="numero font-medium">{brl(totalDoLote)}</strong>
                  </span>
                  <span className="flex gap-2">
                    <button type="button" className="botao-secundario min-h-11 px-3 py-1.5 text-xs sm:min-h-9"
                            onClick={() => setSelecao(new Set())}>
                      Limpar
                    </button>
                    <button type="button" className="botao-primario min-h-11 px-3 py-1.5 text-xs sm:min-h-9"
                            onClick={() => setPagandoLote(true)}>
                      Pagar juntas
                    </button>
                  </span>
                </div>
              </div>
            )}

            {aba === 'abertas' && pagandoLote && selecao.size > 0 && (
              <PainelPagamentoLote
                quantas={selecao.size}
                total={totalDoLote}
                aoFechar={() => setPagandoLote(false)}
                aoPagar={(data, forma) => pagarLote.mutate({ data, forma })}
                pendente={pagarLote.isPending}
                erro={pagarLote.error}
              />
            )}

            {/* A faixa fixa (barra ou painel) não pode cobrir a última linha. */}
            {aba === 'abertas' && selecao.size > 0 && (
              <div className={pagandoLote ? 'h-72' : 'h-14'} />
            )}
          </>
        )}
      </div>

      {/* O FEED. Fora do `space-y-4` de propósito: ele é a segunda coluna da
          grade, não mais um bloco da primeira. */}
      <FeedDeLotes lotes={lotes.data ?? []} carregando={lotes.isPending}
                   erro={lotes.error ? mensagemDeErro(lotes.error) : null}
                   recusas={recusas} cancelando={cancelandoLote}
                   aoCancelar={(lote, motivo) => cancelarLeva.mutate({ lote, motivo })} />
      </div>
    </Layout>
  );
}

/** O LOTE (23/08/2026; refeito em 25/08/2026): N contas, UMA data e UMA forma.
 *
 *  POR QUE ESTE PAINEL É FIXO NA BASE e não inline como os outros da casa:
 *  ele nascia DEPOIS da lista inteira. Quem seleciona contas no meio de uma
 *  lista longa tocava em "Pagar juntas", a barra sumia (ela some quando o
 *  painel abre) e o painel aparecia dezenas de linhas abaixo, fora da tela —
 *  o dono clicava e concluía que o botão não fazia nada (relatado 25/08/2026).
 *  Agora ele abre EXATAMENTE onde o dedo estava, no lugar da barra.
 *
 *  E as formas viraram chips à vista, no lugar do <select>: o dono pediu para
 *  "abrir as opções, ex: dinheiro, pix". Num seletor fechado, a forma default
 *  ficava escondida — e pagar 8 contas de pix lançadas como dinheiro é um
 *  caixa errado que ninguém vê no dia. Visível é mais seguro. */
function PainelPagamentoLote({ quantas, total, aoFechar, aoPagar, pendente, erro }: {
  quantas: number; total: number;
  aoFechar: () => void;
  aoPagar: (data: string, forma: PaymentMethod) => void;
  pendente: boolean;
  erro: unknown;
}) {
  const hoje = hojeISO();
  const ontem = (() => {
    const d = new Date(`${hoje}T12:00:00`);
    d.setDate(d.getDate() - 1);
    return hojeISO(d);
  })();
  const [data, setData] = useState(hoje);
  const [forma, setForma] = useState<PaymentMethod>('pix');

  return (
    <div role="region" aria-label={`Pagar ${quantas} contas juntas`}
         className="fixed inset-x-0 bottom-[calc(3.75rem+env(safe-area-inset-bottom))] z-30 mb-0 max-h-[75svh] overflow-y-auto border-t border-edge-forte bg-panel shadow-lg print:hidden lg:bottom-0 lg:left-60">
      <div className="mx-auto max-w-5xl space-y-3 p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="rotulo">Pagar {quantas} conta{quantas > 1 ? 's' : ''} juntas</h2>
          <p className="numero text-2xl font-medium text-paper">{brl(total)}</p>
        </div>

        <div>
          <p className="rotulo">Como pagou</p>
          <Segmentado<PaymentMethod> toque itens={FORMAS_DO_LOTE} valor={forma} aoMudar={setForma} />
        </div>

        <div>
          <p className="rotulo">Quando pagou</p>
          <div className="flex flex-wrap items-center gap-2">
            <Segmentado toque itens={[{ id: hoje, rotulo: 'Hoje' }, { id: ontem, rotulo: 'Ontem' }]}
                        valor={data} aoMudar={setData} />
            <input type="date" className="campo w-auto py-1 text-xs" value={data} max={hoje}
                   aria-label="Outro dia" onChange={(e) => setData(e.target.value)} />
          </div>
        </div>

        <p className="text-xs text-mute">
          Uma data e uma forma para todas. <strong>Cada conta continua com
          lançamento próprio no caixa</strong> — o extrato mostra{' '}
          {quantas} saída{quantas > 1 ? 's' : ''}, com as descrições. Conta sem
          valor definido não entra (pague-a sozinha, informando o valor).
          {data !== hoje && ' A saída cai no caixa do dia escolhido, não no de hoje.'}
        </p>

        {erro ? <Aviso tipo="erro">{mensagemDeErro(erro)}</Aviso> : null}
        <div className="flex gap-2">
          <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario flex-1 py-2 text-sm"
                  disabled={pendente}
                  onClick={() => aoPagar(data, forma)}>
            {pendente ? 'Lançando…' : `Pagar ${brl(total)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/** A baixa: o valor PAGO é o que vai para o caixa — a estimativa nunca entra. */
function PainelPagamento({ conta, aoFechar, aoPagar, aoMudar }: {
  conta: ContaPagar; aoFechar: () => void; aoPagar: () => void;
  /** A conta mudou (foi paga) sem o painel fechar: a lista recarrega. */
  aoMudar: () => void;
}) {
  const [valor, setValor] = useState(() => {
    const base = conta.valor_real ?? conta.valor_previsto;
    return base == null ? '' : String(base).replace('.', ',');
  });
  const [data, setData] = useState(hojeISO());
  const [forma, setForma] = useState<PaymentMethod>('dinheiro');
  // O comprovante no mesmo passo do pagamento (080): é a hora em que ele está
  // na mão. Opcional — e, se o envio falhar, a conta fica paga do mesmo jeito.
  const [comprovante, setComprovante] = useState<File | null>(null);
  const [anexoFalhou, setAnexoFalhou] = useState<string | null>(null);
  // Vaga do comprovante ocupada e a do boleto livre: o que está lá costuma ser
  // o boleto (anexado antes de existir a vaga dele). Pergunta, e o padrão é sim.
  const podeSerBoleto = !!conta.comprovante && !conta.boleto;
  const [moverParaBoleto, setMoverParaBoleto] = useState(podeSerBoleto);

  const pagar = useMutation({
    mutationFn: async () => {
      // Preparado ANTES de pagar: arquivo que não serve para aqui, sem
      // lançamento no caixa pela metade.
      const anexo = comprovante ? await prepararComprovante(comprovante) : null;
      await pagarConta({ id: conta.id, valor: paraNumero(valor) ?? 0, data, forma });
      if (!anexo) return null;
      try {
        // O arquivo que já estava na vaga do comprovante é o boleto (o dono
        // disse, no aviso abaixo): muda de vaga antes, senão o recibo apagaria.
        if (moverParaBoleto) await mudarVagaDoAnexo(conta.id, 'comprovante');
        await salvarComprovanteConta({
          contaId: conta.id, base64: anexo.base64, contentType: anexo.contentType,
          nome: anexo.nome, tipo: 'comprovante',
        });
        return null;
      } catch (e) {
        return mensagemDeErro(e);
      }
    },
    onSuccess: (falha) => {
      if (!falha) { aoPagar(); return; }
      // Pagou, mas o anexo não subiu: a lista já tem que mostrar a conta paga.
      aoMudar();
      setAnexoFalhou(falha);
    },
  });

  if (anexoFalhou) {
    return (
      <div className="cartao space-y-3 p-4">
        <h2 className="rotulo">Pago: {conta.descricao}</h2>
        <Aviso tipo="alerta">
          A conta foi paga e saiu do caixa, mas o comprovante não subiu: {anexoFalhou} Anexe
          pela linha da conta (+ comprovante).
        </Aviso>
        <button type="button" className="botao-secundario w-full py-2 text-sm" onClick={aoPagar}>Fechar</button>
      </div>
    );
  }

  return (
    <div className="cartao space-y-3 p-4">
      <h2 className="rotulo">Pagar: {conta.descricao}</h2>
      <p className="text-xs text-mute">
        Sai do caixa como {rotulo(conta.categoria)}. Digite o valor que
        <strong> realmente foi pago</strong> — é ele que entra no fechamento,
        não a estimativa.
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className="rotulo" htmlFor="pg-valor">Valor pago (R$)</label>
          <input id="pg-valor" className="campo" value={valor} inputMode="decimal"
                 onChange={(e) => setValor(e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="pg-data">Data</label>
          <input id="pg-data" type="date" className="campo" value={data}
                 onChange={(e) => setData(e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="pg-forma">Forma</label>
          <select id="pg-forma" className="campo" value={forma}
                  onChange={(e) => setForma(e.target.value as PaymentMethod)}>
            <option value="dinheiro">Dinheiro</option>
            <option value="pix">PIX</option>
            <option value="debito">Débito</option>
            <option value="credito">Crédito</option>
            <option value="transferencia">Transferência</option>
            <option value="boleto">Boleto</option>
          </select>
        </div>
      </div>
      <div>
        <label className="rotulo" htmlFor="pg-comprovante">Comprovante (opcional)</label>
        <input id="pg-comprovante" type="file" accept="image/*,application/pdf"
               className="block w-full text-xs text-mute"
               onChange={(e) => setComprovante(e.target.files?.[0] ?? null)} />
        {conta.comprovante && comprovante && (podeSerBoleto ? (
          <label className="mt-1 flex items-start gap-2 text-xs text-mute">
            <input id="pg-mover-boleto" type="checkbox" checked={moverParaBoleto}
                   onChange={(e) => setMoverParaBoleto(e.target.checked)} />
            <span>
              Já há um arquivo anexado ({conta.comprovante.nome ?? 'sem nome'}). É o boleto? Marcado, ele
              vai para a vaga do boleto e fica; desmarcado, este comprovante vai no lugar dele.
            </span>
          </label>
        ) : (
          <p className="mt-1 flex items-center gap-2 text-xs text-mute">
            <span className="ponto text-alerta" aria-hidden="true" />
            A conta já tem um comprovante: este vai no lugar dele.
          </p>
        ))}
      </div>
      {pagar.error ? <Aviso tipo="erro">{mensagemDeErro(pagar.error)}</Aviso> : null}
      <div className="flex gap-2">
        <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={aoFechar}>
          Voltar
        </button>
        <button type="button" className="botao-primario flex-1 py-2 text-sm disabled:opacity-50"
                disabled={pagar.isPending || !paraNumero(valor)}
                onClick={() => pagar.mutate()}>
          {pagar.isPending ? 'Lançando…' : 'Confirmar pagamento'}
        </button>
      </div>
    </div>
  );
}

/** O centro da linha é um BOTÃO (18/09/2026): tocar nele abre o código de
 *  barras embaixo. Pedido do dono com essas palavras — "clicar no centro da
 *  célula da conta". As pontas continuam com o que já tinham: a caixa do
 *  lote à esquerda, valor e ações à direita. */
function CentroDaConta({ conta, aberto, aoTocar }: {
  conta: ContaPagar; aberto: boolean; aoTocar: () => void;
}) {
  return (
    <button type="button" className="min-h-11 min-w-0 flex-1 cursor-pointer text-left"
            aria-expanded={aberto} onClick={aoTocar}>
      <span className="block text-sm font-medium text-paper">
        {conta.descricao}
        {/* 095: nasceu paga, pela tela "Paguei fornecedor" — não é boleto. */}
        {conta.paga_na_hora === true && (
          <span className="selo ml-1.5 align-middle">na hora</span>
        )}
      </span>
      <span className="block text-xs text-mute">
        {rotulo(conta.categoria)} · vence {dataBR(conta.vencimento)}
        {conta.recorrencia_id ? ' · fixa' : ''}
        {/* "atrasada" mora no selo da linha (seloDaConta), com a cor. */}
        {conta.pago_em ? ` · paga em ${dataBR(conta.pago_em)}` : ''}
        {conta.pago_em && conta.forma_pagamento ? ` · ${conta.forma_pagamento}` : ''}
        {conta.pago_em && conta.pago_por_nome ? ` · por ${conta.pago_por_nome}` : ''}
        {/* A linha SEMPRE abre desde 25/09/2026: antes só havia o que mostrar
            quando existia código de barras, e a seta só aparecia nessas. Agora
            o detalhe responde o que é, quanto é, onde foi pago e desenha a
            folha do boleto — vale para toda conta. */}
        <span className="text-signal-soft"> · {aberto ? 'fechar ▴' : 'detalhe ▾'}</span>
      </span>
    </button>
  );
}

/** O código de barras da conta (077): ver, copiar, mandar — ou colar um.
 *
 *  O que se COPIA e se ENVIA é a linha só com números: é o que o aplicativo do
 *  banco aceita colado. A tela mostra agrupada, como no papel, só para ler.
 *  No WhatsApp vai SÓ o número, sem descrição: quem recebe segura o dedo,
 *  copia a mensagem inteira e cola no banco — um texto junto quebraria isso. */
function PainelCodigoBarras({ conta, aoFechar, aoMudar }: {
  conta: ContaPagar; aoFechar: () => void; aoMudar: () => void;
}) {
  // O que acabou de ser gravado vale até a lista recarregar — sem isto, o
  // painel piscava o campo vazio entre o salvar e a volta da consulta.
  const [gravado, setGravado] = useState<string | null | undefined>(undefined);
  const codigo = gravado !== undefined ? gravado : conta.codigo_barras;
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState('');
  const [copiado, setCopiado] = useState<'ok' | 'falhou' | null>(null);

  useEffect(() => {
    if (copiado !== 'ok') return;
    const t = setTimeout(() => setCopiado(null), 2500);
    return () => clearTimeout(t);
  }, [copiado]);

  const salvar = useMutation({
    mutationFn: (linha: string | null) => salvarCodigoBarrasConta({ id: conta.id, codigo: linha }),
    onSuccess: (_, linha) => {
      setGravado(linha);
      setEditando(false);
      setTexto('');
      aoMudar();
    },
  });

  const idCampo = `cb-${conta.id}`;
  if (!codigo || editando) {
    const conferencia = texto.trim() ? conferirCodigo(texto) : null;
    // Vermelho só quando já veio o código inteiro: no meio da digitação, "falta
    // número" é o normal, não um erro.
    const tom = conferencia?.ok ? 'text-ok'
      : soDigitos(texto).length >= 44 ? 'text-erro' : null;
    return (
      <div className="space-y-2">
        <label className="rotulo" htmlFor={idCampo}>Código de barras</label>
        <input id={idCampo} className="campo font-mono text-sm" inputMode="numeric"
               autoComplete="off" autoFocus={editando} value={texto}
               placeholder="cole ou digite os números do boleto"
               onChange={(e) => setTexto(e.target.value)} />
        {/* O sinal no PONTO; o texto fica no tom de apoio (Grafite). */}
        <p className="flex items-start gap-2 text-xs text-mute">
          {tom && <span className={`ponto mt-1 ${tom}`} aria-hidden="true" />}
          {!conferencia
            ? 'Os números que ficam em cima das barras. Pode colar com pontos e espaços.'
            : conferencia.ok
              ? `Confere — ${conferencia.tipo === 'boleto' ? 'boleto de banco' : 'conta de luz, água ou imposto'}.`
              : conferencia.erro}
        </p>
        {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}
        <div className="flex gap-2">
          <button type="button" className="botao-secundario flex-1 py-1.5 text-xs"
                  onClick={() => { if (codigo) setEditando(false); else aoFechar(); }}>
            Voltar
          </button>
          <button type="button" className="botao-primario flex-1 py-1.5 text-xs disabled:opacity-50"
                  disabled={!conferencia?.ok || salvar.isPending}
                  onClick={() => { if (conferencia?.ok) salvar.mutate(conferencia.linha); }}>
            {salvar.isPending ? 'Salvando…' : 'Salvar código'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="rotulo">Código de barras</p>
      <p className="numero select-all break-all text-sm">{formatarLinha(codigo)}</p>
      {/* Copiar e mandar são as duas saídas do mesmo número: secundários lado a
          lado — o primário da tela é o "+ Conta" (Grafite). */}
      <div className="flex gap-2">
        <button type="button" className="botao-secundario min-h-11 flex-1 py-2 text-sm"
                onClick={async () => setCopiado(await copiar(codigo) ? 'ok' : 'falhou')}>
          {copiado === 'ok' ? 'Copiado ✓' : 'Copiar'}
        </button>
        <a className="botao-secundario min-h-11 flex-1 py-2 text-center text-sm"
           href={linkWhatsApp(codigo)} target="_blank" rel="noopener noreferrer">
          Enviar no WhatsApp
        </a>
      </div>
      {copiado === 'falhou' && (
        <p className="flex items-start gap-2 text-xs text-mute">
          <span className="ponto mt-1 text-alerta" aria-hidden="true" />
          O navegador não deixou copiar. Segure o dedo no número acima para selecionar.
        </p>
      )}
      <div className="flex items-center gap-3 text-xs">
        <span className="text-dim">Vai só o número, para copiar com um toque.</span>
        <button type="button" className="ml-auto text-dim underline"
                onClick={() => { setTexto(''); setEditando(true); }}>trocar</button>
        <button type="button" className="text-dim underline" disabled={salvar.isPending}
                onClick={() => salvar.mutate(null)}>tirar</button>
      </div>
      {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}
    </div>
  );
}
